/**
 * React Query hooks for Chat
 */

import { useMutation } from "@tanstack/react-query";
import { chatService } from "../services/chat.service";
import type { ChatMessage, SendChatMessageRequest } from "../types";
import { useState, useEffect } from "react";
import { authService } from "../services/auth.service";

const CHAT_STORAGE_KEY = "ai-assistant-chat-messages";
const CONVERSATION_ID_STORAGE_KEY = "ai-assistant-conversation-id";

export const useChat = () => {
  // Load messages from localStorage on mount
  const loadMessagesFromStorage = (): ChatMessage[] => {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error loading messages from localStorage:", error);
    }
    return [];
  };

  // Load conversationId from localStorage on mount
  const loadConversationIdFromStorage = (): string | undefined => {
    try {
      const stored = localStorage.getItem(CONVERSATION_ID_STORAGE_KEY);
      if (stored) {
        return stored;
      }
    } catch (error) {
      console.error("Error loading conversationId from localStorage:", error);
    }
    return undefined;
  };

  const [messages, setMessages] = useState<ChatMessage[]>(loadMessagesFromStorage);
  const [conversationId, setConversationId] = useState<string | undefined>(loadConversationIdFromStorage);

  // Get firmId from user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
      } else {
        // Only remove from storage when explicitly cleared (handled in clearMessages)
      }
    } catch (error) {
      console.error("Error saving messages to localStorage:", error);
    }
  }, [messages]);

  // Save conversationId to localStorage whenever it changes
  useEffect(() => {
    try {
      if (conversationId) {
        localStorage.setItem(CONVERSATION_ID_STORAGE_KEY, conversationId);
      } else {
        // Only remove from storage when explicitly cleared (handled in clearMessages)
      }
    } catch (error) {
      console.error("Error saving conversationId to localStorage:", error);
    }
  }, [conversationId]);

  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      const params: SendChatMessageRequest = {
        message,
        conversationId,
        firmId,
      };
      return await chatService.sendMessage(params);
    },
    onMutate: async (message) => {
      // Optimistically add user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: message,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      return { userMessage };
    },
    onSuccess: (response, variables, context) => {
      // Add AI response
      const aiMessage: ChatMessage = {
        id: response.message.id || `ai-${Date.now()}`,
        role: "assistant",
        content: response.message.content,
        timestamp: response.message.timestamp || new Date().toISOString(),
        relevantQuestions: response.relevantQuestions || response.message.relevantQuestions,
      };

      setMessages((prev) => [...prev, aiMessage]);
      
      // Update conversation ID if provided
      if (response.conversationId) {
        setConversationId(response.conversationId);
      }
    },
    onError: (error, variables, context) => {
      console.error("Chat error:", error);
      // Remove the optimistically added user message if mutation failed
      if (context?.userMessage) {
        setMessages((prev) => prev.filter((msg) => msg.id !== context.userMessage.id));
      }
      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const sendMessage = (message: string) => {
    if (!message.trim()) return;
    sendMessageMutation.mutate(message);
  };

  const clearMessages = () => {
    setMessages([]);
    setConversationId(undefined);
    // Clear localStorage when user explicitly clears chat
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
      localStorage.removeItem(CONVERSATION_ID_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  };

  return {
    messages,
    sendMessage,
    clearMessages,
    isLoading: sendMessageMutation.isPending,
    error: sendMessageMutation.error,
  };
};
